import React, { useState, useMemo } from 'react';
import { formatRupiah } from '../data/financialData';
import type { Area } from '../data/financialData';
import { useData } from '../DataContext';

interface CashFlowPageProps {
  area: Area;
  branch: string;
  startIdx: number;
  endIdx: number;
}

const CashFlowPage: React.FC<CashFlowPageProps> = ({ area, startIdx, endIdx }) => {
  const { MONTHS, getCashFlowSeries } = useData();
  const [selectedMonth, setSelectedMonth] = useState(endIdx);

  const cf = useMemo(() => getCashFlowSeries(area, startIdx, endIdx), [area, startIdx, endIdx, getCashFlowSeries]);

  const monthData = {
    labels: cf.labels,
    operating: cf.operating[selectedMonth - startIdx] || 0,
    investing: cf.investing[selectedMonth - startIdx] || 0,
    financing: cf.financing[selectedMonth - startIdx] || 0,
    net: cf.net[selectedMonth - startIdx] || 0,
    closingCash: cf.closingCash[selectedMonth - startIdx] || 0,
  };

  return (
    <div className="data-table-card animate-in">
      <div className="data-table-header">
        <div>
          <div className="data-table-title">💰 Cash Flow</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Arus kas bulanan — Area {area}
          </div>
        </div>
      </div>

      <div style={{ padding: '16px', borderBottom: '1px solid var(--border-color)' }}>
        <label style={{ fontSize: '12px', color: 'var(--text-muted)', marginRight: '12px' }}>Bulan</label>
        <select
          value={selectedMonth}
          onChange={e => setSelectedMonth(parseInt(e.target.value))}
          style={{
            padding: '6px 8px',
            fontSize: '13px',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
          }}
        >
          {MONTHS.slice(startIdx, endIdx + 1).map((m, i) => (
            <option key={startIdx + i} value={startIdx + i}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
        <div style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', padding: '12px', borderRadius: '6px', border: '1px solid #10b981' }}>
          <div style={{ fontSize: '11px', color: '#10b981', fontWeight: '600' }}>Operating CF</div>
          <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#10b981', marginTop: '4px' }}>
            {formatRupiah(monthData.operating)}
          </div>
        </div>
        <div style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', padding: '12px', borderRadius: '6px', border: '1px solid #3b82f6' }}>
          <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: '600' }}>Investing CF</div>
          <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#3b82f6', marginTop: '4px' }}>
            {formatRupiah(monthData.investing)}
          </div>
        </div>
        <div style={{ backgroundColor: 'rgba(168, 85, 247, 0.1)', padding: '12px', borderRadius: '6px', border: '1px solid #a855f7' }}>
          <div style={{ fontSize: '11px', color: '#a855f7', fontWeight: '600' }}>Financing CF</div>
          <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#a855f7', marginTop: '4px' }}>
            {formatRupiah(monthData.financing)}
          </div>
        </div>
        <div style={{ backgroundColor: 'rgba(6, 182, 212, 0.1)', padding: '12px', borderRadius: '6px', border: '1px solid #06b6d4' }}>
          <div style={{ fontSize: '11px', color: '#06b6d4', fontWeight: '600' }}>Net Change</div>
          <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#06b6d4', marginTop: '4px' }}>
            {formatRupiah(monthData.net)}
          </div>
        </div>
        <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '12px', borderRadius: '6px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Closing Cash</div>
          <div style={{ fontSize: '15px', fontWeight: 'bold', marginTop: '4px' }}>
            {formatRupiah(monthData.closingCash)}
          </div>
        </div>
      </div>

      <div style={{ padding: '16px', overflowX: 'auto' }}>
        <table className="data-table" style={{ width: '100%', fontSize: '12px' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Bulan</th>
              <th style={{ textAlign: 'right', color: '#10b981' }}>Operating</th>
              <th style={{ textAlign: 'right', color: '#3b82f6' }}>Investing</th>
              <th style={{ textAlign: 'right', color: '#a855f7' }}>Financing</th>
              <th style={{ textAlign: 'right', color: '#06b6d4' }}>Net Change</th>
              <th style={{ textAlign: 'right' }}>Closing Cash</th>
            </tr>
          </thead>
          <tbody>
            {cf.labels.map((label, i) => (
              <tr key={i}>
                <td style={{ textAlign: 'left' }}>{label}</td>
                <td style={{ textAlign: 'right' }}>{formatRupiah(cf.operating[i])}</td>
                <td style={{ textAlign: 'right' }}>{formatRupiah(cf.investing[i])}</td>
                <td style={{ textAlign: 'right' }}>{formatRupiah(cf.financing[i])}</td>
                <td style={{ textAlign: 'right' }}>{formatRupiah(cf.net[i])}</td>
                <td style={{ textAlign: 'right' }}>{formatRupiah(cf.closingCash[i])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CashFlowPage;
